import { creativeSaveErrorKey } from "@/lib/api/creative-library-errors";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Check, Library, Loader2 } from "@digital-twins/design-system/icons";
import type {
  CreativeMaterialRole,
  CreativeSourceDefaults,
} from "@digital-twins/shared-types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/Text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import { useCreativeLibrary } from "@/hooks/useCreativeLibrary";
import { CreativeFilingFields } from "./CreativeFilingFields";
import { CreativeProvenance } from "./CreativeProvenance";
import type { SaveCreativeButtonProps } from "@/types/creative-library";

export function SaveCreativeButton({
  assetToken,
  title: initialTitle,
  className,
}: SaveCreativeButtonProps) {
  const { t } = useTranslation("app");
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(initialTitle);
  const [role, setRole] = useState<CreativeMaterialRole>("composition");
  const [brandToken, setBrandToken] = useState("none");
  const [productToken, setProductToken] = useState("none");
  const [source, setSource] = useState<CreativeSourceDefaults | null>(null);
  const [loadingSource, setLoadingSource] = useState(false);
  const [sourceError, setSourceError] = useState(false);
  const [reloadSource, setReloadSource] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedCreativeToken, setSavedCreativeToken] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<ReturnType<
    typeof creativeSaveErrorKey
  > | null>(null);
  const library = useCreativeLibrary(open);
  const filingTouched = useRef(false);
  useEffect(() => {
    if (!open) return;
    let active = true;
    setSource(null);
    setSourceError(false);
    setLoadingSource(true);
    setError(null);
    void creativeLibraryApi
      .sourceDefaults(assetToken)
      .then((defaults) => {
        if (!active) return;
        setSource(defaults);
        if (!filingTouched.current) {
          setBrandToken(defaults.brandToken ?? "none");
          setProductToken(defaults.productToken ?? "none");
        }
      })
      .catch(() => {
        if (active) setSourceError(true);
      })
      .finally(() => {
        if (active) setLoadingSource(false);
      });
    return () => {
      active = false;
    };
  }, [assetToken, open, reloadSource]);
  async function save() {
    if (!source) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await creativeLibraryApi.saveThreadAsset({
        assetToken,
        title: title.trim(),
        role,
        brandToken: brandToken === "none" ? null : brandToken,
        productToken: productToken === "none" ? null : productToken,
      });
      setSavedCreativeToken(saved.token);
      const matchesFiling =
        saved.title === title.trim() &&
        saved.role === role &&
        saved.brandToken === (brandToken === "none" ? null : brandToken) &&
        saved.productToken === (productToken === "none" ? null : productToken);
      if (matchesFiling) setOpen(false);
    } catch (error) {
      setError(creativeSaveErrorKey(error));
    } finally {
      setSaving(false);
    }
  }
  const busy = saving || loadingSource || library.loading;
  const existingToken =
    savedCreativeToken ?? source?.savedCreativeToken ?? null;
  const viewHref = `/knowledge?view=creatives&creative=${encodeURIComponent(existingToken ?? "")}`;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!saving) {
          if (next) filingTouched.current = false;
          setOpen(next);
        }
      }}
    >
      {existingToken && !open ? (
        <Button variant="ghost" size="sm" asChild className={className}>
          <a href={viewHref} data-testid={`view-creative-${assetToken}`}>
            <Check className="h-4 w-4" />
            {t("creativeLibrary.viewInCreatives")}
          </a>
        </Button>
      ) : (
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={className}
            data-testid={`save-creative-${assetToken}`}
          >
            <Library className="h-4 w-4" />
            {t("creativeLibrary.saveToCreatives")}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent
        className="max-h-[90dvh] max-w-xl overflow-y-auto"
        data-testid="save-creative-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {t(
              existingToken
                ? "creativeLibrary.alreadySaved"
                : "creativeLibrary.saveToCreatives",
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              existingToken
                ? "creativeLibrary.alreadySavedHint"
                : "creativeLibrary.savePrivateHint",
            )}
          </DialogDescription>
        </DialogHeader>
        {existingToken ? (
          <div className="flex flex-col gap-4">
            {source?.provenance && (
              <CreativeProvenance provenance={source.provenance} />
            )}
            <Button asChild>
              <a href={viewHref} data-testid="view-existing-creative">
                {t("creativeLibrary.viewInCreatives")}
              </a>
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor={`save-title-${assetToken}`}>
                {t("creativeLibrary.title")}
              </Label>
              <Input
                id={`save-title-${assetToken}`}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                data-testid="save-creative-title"
                disabled={saving}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("creativeLibrary.referenceRole")}</Label>
              <Select
                value={role}
                onValueChange={(value) =>
                  setRole(value as CreativeMaterialRole)
                }
                disabled={busy}
              >
                <SelectTrigger
                  aria-label={t("creativeLibrary.referenceRole")}
                  data-testid="save-creative-role"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(["product", "logo", "style", "composition"] as const).map(
                    (value) => (
                      <SelectItem key={value} value={value}>
                        {t(`creativeLibrary.roles.${value}`)}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>
            <CreativeFilingFields
              brands={library.brands}
              products={library.products}
              brandToken={brandToken}
              productToken={productToken}
              disabled={busy || !source}
              testIdPrefix="save-creative"
              onChange={(brand, product) => {
                filingTouched.current = true;
                setBrandToken(brand);
                setProductToken(product);
              }}
            />
            <Text size="xs" color="muted">
              {t("creativeLibrary.filingHint")}
            </Text>
            {source?.provenance && (
              <CreativeProvenance provenance={source.provenance} />
            )}
            {(sourceError || library.error) && (
              <div
                className="flex items-center justify-between gap-3"
                role="alert"
              >
                <Text size="sm" className="text-destructive">
                  {t("creativeLibrary.sourceLoadFailed")}
                </Text>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setReloadSource((value) => value + 1);
                    void library.reload();
                  }}
                >
                  {t("creativeLibrary.retry")}
                </Button>
              </div>
            )}
            {error && (
              <Text
                size="sm"
                className="flex items-center gap-2 text-destructive"
                role="alert"
              >
                <AlertCircle className="h-4 w-4" />
                {t(error)}
              </Text>
            )}
            <Button
              onClick={() => void save()}
              disabled={busy || !source || library.error || !title.trim()}
              data-testid="save-creative-confirm"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("creativeLibrary.saveToCreatives")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
